import { Mountain, Users, Calendar, Package, MapPin, Phone, Globe, Wallet } from 'lucide-react';

// Composants de base réutilisables
export const InfoItem = ({ label, value, icon, config }) => {
  const mergedConfig = { ...config };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      fontFamily: mergedConfig.fontFamily
    }}>
      <div style={{
        fontSize: mergedConfig.fontSize * 0.9,
        color: mergedConfig.primaryColor,
        fontWeight: 500
      }}>
        {label}
      </div>
      <div style={{
        fontSize: mergedConfig.fontSize,
        color: mergedConfig.textColor || '#1f2937',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        {icon} {value}
      </div>
    </div>
  );
};

export const ActivitySection = ({ data, config, t, formatDate, formatDateTime, getServiceIcon }) => {
  // Utiliser les données de la section
  const activityData = data.booking_activity || data;
  const productData = data.product || {};

  // Calcul du total des passagers
  const totalPassengers = (activityData?.adult || 0) +
    (activityData?.child || 0) +
    (activityData?.baby || 0) +
    (activityData?.senior || 0);

  // Format du prix
  const formatPrice = (price) => {
    if (!price) return '0.00';
    return parseFloat(price).toFixed(2);
  };

  // Devise
  const currency = data.currency || 'EUR';

  return (
    <div className='pdf-section' style={{
      background: 'transparent',
      marginBottom: config.gap
    }}>
      <div style={{
        marginBottom: 0,
        fontFamily: config.fontFamily,
        background: config.sectionColor,
        padding: '20px',
        borderRadius: '8px',
        border: `1px solid ${config.sectionColor}40`
      }}>
        <div style={{
          fontSize: `calc(${config.fontSize || 18}px * 1.5)`,
          fontWeight: 'bold',
          color: config.primaryColor,
          marginBottom: 20,
          paddingBottom: 8,
          borderBottom: `2px solid ${config.primaryColor}`,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          {getServiceIcon ? getServiceIcon('activity') : <Mountain size={20} />}
          {data.name || t('activityInformation')}
        </div>

        {/* Informations générales sur l'activité */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: config.columns > 1 ? `repeat(${config.columns}, 1fr)` : '1fr',
          gap: 20,
          marginBottom: 24
        }}>
          {/* Nom de l'activité */}
          <InfoItem
            config={config}
            label={t('activityName')}
            value={productData.name || t('notAvailable')}
            icon={<Package size={16} />}
          />

          {/* Durée */}
          <InfoItem
            config={config}
            label={t('duration')}
            value={`${activityData?.days || 0} ${t('days')}`}
            icon={<Calendar size={16} />}
          />

          {/* Prix total */}
          <InfoItem
            config={config}
            label={t('totalPrice')}
            value={`${formatPrice(activityData?.amount)} ${currency}`}
            icon={<Wallet size={16} />}
          />

          {/* Pays */}
          {productData.country?.name && (
            <InfoItem
              config={config}
              label={t('country')}
              value={productData.country.name}
              icon={<Globe size={16} />}
            />
          )}

          {/* Ville */}
          {productData.city?.name && (
            <InfoItem
              config={config}
              label={t('city')}
              value={productData.city.name}
              icon={<MapPin size={16} />}
            />
          )}

          {/* Téléphone */}
          {productData.phone && (
            <InfoItem
              config={config}
              label={t('contactPhone')}
              value={productData.phone}
              icon={<Phone size={16} />}
            />
          )}
        </div>

        {/* Détails des passagers */}
        <div style={{
          background: config.backgroundColor,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: 24,
          border: `1px solid ${config.sectionColor}60`
        }}>
          <h3 style={{
            fontSize: config.subsectionTitleSize || 16,
            fontWeight: config.subsectionTitleWeight || 600,
            color: config.primaryColor,
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <Users size={18} />
            {t('participantBreakdown')}
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: config.participantsLayout || 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: config.participantsGap || 16,
            marginBottom: 16
          }}>
            {/* Adults */}
            <div style={{
              background: config.sectionColor,
              borderRadius: '10px',
              padding: '16px',
              textAlign: 'center',
              border: `1px solid ${config.sectionColor}60`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                fontSize: '13px',
                color: config.secondaryColor,
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                {t('adults')}
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                lineHeight: '1',
                color: config.textColor || '#1f2937'
              }}>
                {activityData?.adult || 0}
              </div>
            </div>

            {/* Children */}
            <div style={{
              background: config.sectionColor,
              borderRadius: '10px',
              padding: '16px',
              textAlign: 'center',
              border: `1px solid ${config.sectionColor}60`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                fontSize: '13px',
                color: config.secondaryColor,
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                {t('children')}
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                lineHeight: '1',
                color: config.textColor || '#1f2937'
              }}>
                {activityData?.child || 0}
              </div>
            </div>

            {/* Babies */}
            <div style={{
              background: config.sectionColor,
              borderRadius: '10px',
              padding: '16px',
              textAlign: 'center',
              border: `1px solid ${config.sectionColor}60`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                fontSize: '13px',
                color: config.secondaryColor,
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                {t('babies')}
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                lineHeight: '1',
                color: config.textColor || '#1f2937'
              }}>
                {activityData?.baby || 0}
              </div>
            </div>

            {/* Seniors */}
            <div style={{
              background: config.sectionColor,
              borderRadius: '10px',
              padding: '16px',
              textAlign: 'center',
              border: `1px solid ${config.sectionColor}60`,
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                fontSize: '13px',
                color: config.secondaryColor,
                marginBottom: '8px',
                fontWeight: '500'
              }}>
                {t('seniors')}
              </div>
              <div style={{
                fontSize: '28px',
                fontWeight: 'bold',
                lineHeight: '1',
                color: config.textColor || '#1f2937'
              }}>
                {activityData?.senior || 0}
              </div>
            </div>
          </div>

          {/* Total */}
          <div style={{
            background: config.sectionColor,
            borderRadius: '10px',
            padding: '16px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${config.primaryColor}40`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            maxWidth: '200px',
            margin: '0 auto'
          }}>
            <div style={{
              fontSize: '13px',
              marginBottom: '8px',
              fontWeight: '600',
              color: config.primaryColor
            }}>
              {t('totalParticipants')}
            </div>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              lineHeight: '1',
              color: config.primaryColor
            }}>
              {totalPassengers}
            </div>
          </div>
        </div>

        {/* Informations de localisation */}
        {productData.address && (
          <div style={{
            marginBottom: 24,
            padding: '20px',
            background: config.backgroundColor,
            borderRadius: '10px',
            border: `1px solid ${config.sectionColor}40`
          }}>
            <h3 style={{
              fontSize: config.subsectionTitleSize || 16,
              fontWeight: config.subsectionTitleWeight || 600,
              color: config.primaryColor,
              margin: '0 0 12px 0',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <MapPin size={18} />
              {t('locationDetails')}
            </h3>
            <div style={{
              fontSize: '14px',
              color: config.textColor || '#4b5563',
              lineHeight: '1.6'
            }}>
              {productData.address}
            </div>
            {(productData.latitude && productData.longitude) && (
              <div style={{
                fontSize: '12px',
                color: config.secondaryColor,
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                {t('coordinates')}: {productData.latitude}, {productData.longitude}
              </div>
            )}
          </div>
        )}

        {/* Prix détaillés */}
        {activityData?.prices && activityData.prices.length > 0 && (
          <div style={{
            marginBottom: 24,
            padding: '20px',
            background: config.sectionColor,
            borderRadius: '10px',
            border: `1px solid ${config.sectionColor}60`
          }}>
            <h3 style={{
              fontSize: config.subsectionTitleSize || 16,
              fontWeight: config.subsectionTitleWeight || 600,
              color: config.primaryColor,
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              {t('detailedPricing')}
            </h3>

            <div style={{
              background: config.backgroundColor,
              borderRadius: '8px',
              padding: '16px',
              border: `1px solid ${config.sectionColor}40`
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{
                    borderBottom: `2px solid ${config.sectionColor}40`
                  }}>
                    <th style={{
                      padding: '12px',
                      textAlign: 'left',
                      fontSize: config.tableHeaderSize || 14,
                      fontWeight: config.tableHeaderWeight || 600,
                      color: config.textColor || '#374151'
                    }}>
                      {t('description')}
                    </th>
                    <th style={{
                      padding: '12px',
                      textAlign: 'right',
                      fontSize: config.tableHeaderSize || 14,
                      fontWeight: config.tableHeaderWeight || 600,
                      color: config.textColor || '#374151'
                    }}>
                      {t('amount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activityData.prices.map((price, index) => (
                    <tr key={index} style={{
                      borderBottom: `1px solid ${config.sectionColor}20`
                    }}>
                      <td style={{
                        padding: '12px',
                        fontSize: config.tableCellSize || 14,
                        color: config.textColor || '#4b5563'
                      }}>
                        {price.description || `${t('priceItem')} ${index + 1}`}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontSize: config.tableCellSize || 14,
                        fontWeight: config.tableCellWeight || 500,
                        color: config.successColor || '#10b981'
                      }}>
                        {formatPrice(price.amount)} {currency}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Message si pas de prix détaillés */}
        {(!activityData?.prices || activityData.prices.length === 0) && (
          <div style={{
            padding: '16px',
            background: config.sectionColor,
            borderRadius: '8px',
            textAlign: 'center',
            border: `1px solid ${config.sectionColor}60`
          }}>
            <div style={{
              fontSize: '14px',
              color: config.textColor || '#92400e',
              fontWeight: '500'
            }}>
              {t('priceIncludesAll')}
            </div>
            <div style={{
              fontSize: '13px',
              color: config.secondaryColor,
              marginTop: '4px'
            }}>
              {t('totalAmount')}: {formatPrice(activityData?.amount)} {currency}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Mettre à jour le mapping SectionComponents pour inclure ce composant
export const ActivitySectionComponents = {
  ActivitySection,
};

// Fonction utilitaire pour obtenir un composant par nom
export const getActivitySectionComponent = (componentName) => {
  return ActivitySectionComponents[componentName] || null;
};